import SEO, { getBreadcrumbSchema } from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const OwnersFAQ = () => {
  const faqs = [
    {
      question: "What are your property management fees?",
      answer: "Our standard management fee is 8% of collected rent for single-family homes in Overland Park, Leawood, Lenexa, and Olathe. However, we offer competitive rates based on portfolio volume and our relationship with you. We also charge a one-time placement fee equal to one month's rent when we place a new tenant. There are no hidden fees - maintenance coordination, inspections, and reporting are all included. Contact us to discuss custom pricing for multi-property portfolios."
    },
    {
      question: "How quickly can you fill my vacancy?",
      answer: "We fill vacancies fast. We start marketing immediately with professional photos, competitive pricing analysis, and syndication to 50+ rental platforms. Our aggressive approach ensures your property gets maximum exposure from day one. Most well-maintained properties in Overland Park and Leawood receive multiple applications within the first week."
    },
    {
      question: "What is your tenant screening process?",
      answer: "We conduct comprehensive screening including: criminal background check, credit report review (minimum 600 score), employment verification, previous landlord references, and income verification (3x monthly rent minimum). Our rigorous screening results in a less than 2% eviction rate."
    },
    {
      question: "How do I receive my rental income?",
      answer: "Rent is collected electronically on the 1st of each month. Owner distributions are processed by ACH direct deposit to your bank account by the 10th of each month, along with a detailed statement of income and expenses. You can view transactions in real-time via your owner portal."
    },
    {
      question: "Who pays for maintenance and repairs?",
      answer: "Owners are responsible for all maintenance and repair costs. However, we coordinate everything and use our vetted contractor network to ensure fair pricing. We handle emergency repairs up to $500 immediately and contact you for approval on larger expenses. Regular preventive maintenance helps avoid costly emergency repairs."
    },
    {
      question: "What happens if my tenant doesn't pay rent?",
      answer: "We follow a strict collections process: late fees are assessed on the 6th, formal notices on the 10th, and eviction proceedings begin if rent remains unpaid by the 15th. We handle all legal filings in compliance with Kansas law. Our proactive approach maintains a 99% rent collection rate."
    },
    {
      question: "How often do you inspect my property?",
      answer: "We conduct an initial move-in inspection with the tenant, an annual mid-lease inspection, and a move-out inspection. All inspections include photo documentation. We also perform drive-by exterior inspections quarterly to monitor property condition in Overland Park and surrounding areas."
    },
    {
      question: "Can I terminate the management agreement?",
      answer: "Yes, our management agreement requires 30 days written notice from either party. If you terminate while a tenant is in place, there's no early termination fee. We'll provide all tenant files and coordinate a smooth transition."
    },
    {
      question: "Do you manage properties outside Johnson County?",
      answer: "We focus exclusively on the Kansas side of Kansas City metro, primarily Johnson County cities including Overland Park, Leawood, Lenexa, Olathe, Shawnee, Prairie Village, Mission, and Merriam. This focused approach allows us to provide superior local market expertise."
    },
    {
      question: "What if I want to sell my rental property?",
      answer: "We'll work with you and your real estate agent to coordinate showings while respecting tenant rights under Kansas law. We can also help you evaluate whether selling with a tenant in place or vacant makes more financial sense based on current Johnson County market conditions."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Property Owner FAQ | Rently Properties Kansas City"
        description="Common questions about property management in Overland Park, Leawood, Lenexa & Olathe. Learn about fees, tenant screening, maintenance, rent collection in Johnson County KS."
        canonical="/owners-faq"
        keywords="property management FAQ, Kansas City landlord questions, Johnson County property manager fees, tenant screening process, rent collection FAQ, Overland Park property management"
        schema={[
          faqSchema,
          getBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Owner FAQ", path: "/owners-faq" }
          ])
        ]}
      />
      <Header />

      <main className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Property Owner <span className="text-primary">FAQ</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Common questions from Kansas City metro property owners
            </p>
          </div>

          <Card>
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          <div className="text-center mt-12">
            <p className="text-muted-foreground mb-6">
              Still have questions? We're here to help.
            </p>
            <Button size="lg" className="bg-secondary hover:bg-secondary/90" asChild>
              <Link to="/#contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OwnersFAQ;
